import {
  useState,
  useEffect,
  type FC,
  type ComponentType,
} from "react";
import { Badge } from "react-bootstrap";
import BootstrapButton, { type ButtonProps } from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FormControl from "react-bootstrap/FormControl";
import InputGroup from "react-bootstrap/InputGroup";

const Button: ComponentType<ButtonProps> = BootstrapButton;

type GameRoom = {
  roomId: string,
  numClients: number,
  maxClients: number,
  displayName: string,
}

type GamePlayer = {
  sessionId: string;
  name: string;
  isGm: boolean;
};

type FabulaUltimaJoinGamePanelProps = {
  gameIsOngoing: boolean,
  rooms: ReadonlyArray<GameRoom>,
  handleJoinGame: (user: string, roomId: string, isGm: boolean) => void;
  handleLeaveGame: () => void;
  players: ReadonlyArray<GamePlayer>;
};

const FabulaUltimaJoinGamePanel: FC<FabulaUltimaJoinGamePanelProps> = (props) => {
  const [userName, setUserName] = useState<string>("");
  const [isGm, setIsGm] = useState<boolean>(false);
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");

  // 设置默认选中的房间
  useEffect(() => {
    if (props.rooms.length > 0 && selectedRoomId === "") {
      setSelectedRoomId(current => current || props.rooms[0].roomId);
    }
  }, [props.rooms, selectedRoomId]);

  let inner = null;

  if (props.gameIsOngoing) {
    inner = <div className="fu-session-toolbar">
      <div className="fu-player-roster" aria-label="房间成员">
        <span className="fu-player-roster-label">房间成员 <b>{props.players.length}</b></span>
        <ul className="fu-player-list">
          {props.players.map(player => <li
            className={`fu-player-nameplate${player.isGm ? ' fu-player-nameplate--gm' : ''}`}
            key={player.sessionId} aria-label={player.isGm ? `${player.name}，GM` : player.name}>
            <span className="fu-player-seal" aria-hidden="true">{player.isGm ? '♛' : Array.from(player.name.trim())[0] || '✦'}</span>
            <span className="fu-player-display-name">{player.name}</span>
            {player.isGm && <span className="fu-player-gm-badge">GM</span>}
          </li>)}
        </ul>
      </div>
      {isGm && <Badge bg="warning" className="mx-2 fs-6">GM模式</Badge>}
      <Button className="fu-leave-button" variant="danger" onClick={props.handleLeaveGame}>离开游戏</Button>
    </div>
  } else {
    inner = <>
      <div className="fu-lobby-room-selection">
        <h5>选择房间</h5>
        <p className="text-muted small mb-2">
          点击房间名称选择要加入的房间。
        </p>
        <div className="list-group">
          {
            props.rooms.map(room => {
              const isSelected = selectedRoomId === room.roomId;
              const isFull = room.numClients >= room.maxClients;
              const isDisabled = isFull && !isGm; // GM可以加入满员房间
              
              return (
                <button
                  type="button"
                  key={room.roomId}
                  disabled={isDisabled}
                  aria-pressed={isSelected}
                  aria-label={room.displayName}
                  className={`list-group-item list-group-item-action ${
                    isSelected ? 'active' : ''
                  } ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && setSelectedRoomId(room.roomId)}
                >
                  <span className="fu-room-sigil" aria-hidden="true" />
                  <div className="d-flex w-100 justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">
                        {room.displayName}
                      </h6>
                      <small>ID: <code>{room.roomId}</code></small>
                    </div>
                    <div className="text-end">
                      <Badge 
                        bg={isFull ? 'danger' : 'success'}
                        className="mb-1"
                      >
                        {room.numClients}/{room.maxClients}
                      </Badge>
                    </div>
                  </div>
                  <span className="fu-room-choice-state">{isSelected ? '已选择' : isFull ? (isGm ? '已满员 · GM 可加入' : '房间已满') : '选择此房间'}</span>
                </button>
              );
            })
          }
        </div>
        {selectedRoomId && (
          <div className="mt-2">
            <small className="text-info">
              已选择: {props.rooms.find(r => r.roomId === selectedRoomId)?.displayName}
            </small>
          </div>
        )}
      </div>
      <InputGroup>
        <InputGroup.Text id="fu-join-name-label">用户名</InputGroup.Text>
        <FormControl
          aria-labelledby="fu-join-name-label"
          placeholder="输入你的名字"
          onChange={e => setUserName(e.target.value)}
          value={userName} />
      </InputGroup>

      <div className="fu-lobby-actions"><Form.Check
        id="fu-join-gm"
        inline
        type="switch"
        label="我是GM"
        checked={isGm}
        onChange={e => setIsGm(e.target.checked)}
      />
      <Button
        className="fu-join-button"
        variant={isGm? "warning" : "primary"}
        disabled={userName === "" || selectedRoomId === ""}
        onClick={() => props.handleJoinGame(userName, selectedRoomId, isGm)}>
        加入游戏
      </Button>
      </div>
    </>;
  }
  return <div className={`fu-room-panel ${props.gameIsOngoing ? 'fu-room-panel--joined' : 'fu-room-panel--lobby'}`}>
    <div className="fu-room-brand"><span aria-hidden="true">✧</span> FABULA ULTIMA <small>战斗模拟器</small></div>
    <div className="fu-room-controls">{inner}</div>
  </div>;
};

export {
  type GameRoom,
  type GamePlayer,
  FabulaUltimaJoinGamePanel,
};
